import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, filter, NEVER, Observable, switchMap, tap } from 'rxjs';

import { SOCKET_ROOM } from '../../../../enums/events-buffer/socket/socket-rooms-enum';
import { SOCKET_EVENT } from '../enum/socket-event.enum';

import { DEVICE } from '../../../../../shared/domain/tokens/device.token';
import { EVENT_BUS } from '../../../../../shared/domain/tokens/event-bus.token';
import { LOGGER_SERVICE } from '../../../../../shared/domain/tokens/logger.token';
import { SOCKET_ROOM_HANDLER } from '../../../../../shared/domain/tokens/socket-room-handler.token';
import { SOCKET_SERVICE } from '../../../../../shared/domain/tokens/socket-service.token';

import { BookingReceivedEvent } from '../../../../../shared/application/events/booking/booking-received.event';
import { IBookingSocketPayload } from '../interfaces/socket-payload.interface';
import { BookingFeatureFlagService } from '../services/booking-feature-flag.service';

@Injectable({ providedIn: 'root' })
export class BookingSocketListenerService {
  private readonly logger = inject(LOGGER_SERVICE);
  private readonly eventBus = inject(EVENT_BUS);
  private readonly socket = inject(SOCKET_SERVICE);
  private readonly roomHandler = inject(SOCKET_ROOM_HANDLER);
  private readonly device = inject(DEVICE);
  private readonly featureFlag = inject(BookingFeatureFlagService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.roomHandler.listenToRoom(SOCKET_ROOM.booking).pipe(
      filter(() => this.featureFlag.isWebSocketsEnabled()),
      switchMap(() => this.socket.receive<IBookingSocketPayload>(SOCKET_EVENT.updated)),
      filter(({ payload }) => this.isValidRemoteBookingUpdate(payload.booking.deviceId)),
      tap(({ payload }) => this.publishBookingUpdate(payload.booking.id)),
      catchError(error => this.handleError(error)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  private isValidRemoteBookingUpdate(originDeviceId?: string): boolean {
    const currentDeviceId = this.device.getDeviceInfo()?.id ?? '';
    const isExternal = originDeviceId !== currentDeviceId;

    if (!isExternal) {
      this.logger.debug(`[Socket][Booking] Ignored self-originated update (deviceId: ${currentDeviceId})`);
    }

    return isExternal;
  }

  private publishBookingUpdate(bookingId: string): void {
    this.eventBus.publish(new BookingReceivedEvent({ bookingId }));
    this.logger.debug(`[Socket][Booking] Published booking update event for bookingId: ${bookingId}`);
  }

  private handleError(error: Error): Observable<never> {
    this.logger.error(error, '[Socket][Booking] Error processing booking update');

    return NEVER;
  }
}
